// Currencies offered for supplier defaults and purchase orders. Codes are stable identifiers (not
// user-facing prose); their display names are localized at render time via Intl.DisplayNames rather
// than hardcoded, so we don't maintain a translated currency-name table per locale.
export const SUPPORTED_PURCHASE_CURRENCY_CODES = ['ISK', 'DKK', 'NOK', 'SEK', 'EUR'] as const

export const getCurrencyOptions = (locale: string): { value: string; label: string }[] => {
	let displayNames: Intl.DisplayNames | undefined
	try {
		displayNames = new Intl.DisplayNames([locale], { type: 'currency' })
	} catch {
		displayNames = undefined
	}
	return SUPPORTED_PURCHASE_CURRENCY_CODES.map(code => {
		const name = displayNames?.of(code)
		return { value: code, label: name ? `${code} - ${name}` : code }
	})
}

// Where a suggested purchase currency came from, ordered by confidence. 'tenant' is a generic
// fallback (the reporting currency) rather than something specific to this order, so the UI can
// present it differently (ask the user to confirm) from a supplier/location-derived suggestion.
export type PurchaseCurrencySource = 'purchaseOrder' | 'supplier' | 'location' | 'tenant'

export const resolvePurchaseOrderCurrency = (args: {
	purchaseOrderCurrencyCode?: string
	supplierCurrencyCode?: string
	locationCurrencyCode?: string
	tenantDefaultCurrencyCode?: string
}): { currencyCode: string; source: PurchaseCurrencySource } | undefined => {
	if (args.purchaseOrderCurrencyCode) return { currencyCode: args.purchaseOrderCurrencyCode, source: 'purchaseOrder' }
	if (args.supplierCurrencyCode) return { currencyCode: args.supplierCurrencyCode, source: 'supplier' }
	if (args.locationCurrencyCode) return { currencyCode: args.locationCurrencyCode, source: 'location' }
	if (args.tenantDefaultCurrencyCode) return { currencyCode: args.tenantDefaultCurrencyCode, source: 'tenant' }
	return undefined
}
